import { useState } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { Select } from './Select';
import { FileDropzone } from './FileDropzone';
import { computeHash } from '../services/hashService';
import { HashAlgorithm, EncodingFormat, HashResult } from '../types';
import { translations } from '../i18n/hu';
import { Copy, Check, Download } from 'lucide-react';

const algorithms: { value: HashAlgorithm; label: string }[] = [
  { value: 'MD5', label: 'MD5' },
  { value: 'SHA-1', label: 'SHA-1' },
  { value: 'SHA-256', label: 'SHA-256' },
  { value: 'SHA-384', label: 'SHA-384' },
  { value: 'SHA-512', label: 'SHA-512' },
  { value: 'SHA3-256', label: 'SHA3-256' },
  { value: 'SHA3-512', label: 'SHA3-512' },
  { value: 'BLAKE2b', label: 'BLAKE2b' },
  { value: 'BLAKE2s', label: 'BLAKE2s' },
  { value: 'BLAKE3', label: 'BLAKE3' },
  { value: 'CRC32', label: 'CRC32' },
  { value: 'xxHash64', label: 'xxHash64' },
];

const encodings: { value: EncodingFormat; label: string }[] = [
  { value: 'hex', label: translations.encoding.hex },
  { value: 'hex-upper', label: translations.encoding.hexUpper },
  { value: 'base64', label: translations.encoding.base64 },
];

export function FileHash() {
  const [files, setFiles] = useState<File[]>([]);
  const [algorithm, setAlgorithm] = useState<HashAlgorithm>('SHA-256');
  const [encoding, setEncoding] = useState<EncodingFormat>('hex');
  const [results, setResults] = useState<HashResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [error, setError] = useState<string>('');

  const handleFilesSelected = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
    setResults([]);
  };

  const handleGenerate = async () => {
    if (files.length === 0) {
      setError('Kérjük, válasszon ki legalább egy fájlt');
      return;
    }

    setIsLoading(true);
    setError('');
    setResults([]);
    setProgress(0);

    try {
      const newResults: HashResult[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress(((i + 1) / files.length) * 100);
        
        const result = await computeHash(file, algorithm, encoding);
        newResults.push(result);
        setResults([...newResults]);
      }

      setProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba történt');
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleCopy = (index: number, hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleDownloadChecksum = (result: HashResult) => {
    const extension = result.algorithm.toLowerCase().replace('/', '-');
    const content = `${result.hash}  ${result.fileName || 'file'}\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.fileName || 'file'}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAllChecksums = () => {
    const content = results
      .map(r => `${r.hash}  ${r.fileName || 'file'}\n`)
      .join('');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'checksums.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card title="Fájl Hash Generálás">
        <div className="space-y-4">
          <FileDropzone
            onFilesSelected={handleFilesSelected}
            multiple={true}
            maxSize={4096}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={translations.algorithm.label}
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value as HashAlgorithm)}
              options={algorithms}
            />
            <Select
              label={translations.encoding.label}
              value={encoding}
              onChange={(e) => setEncoding(e.target.value as EncodingFormat)}
              options={encodings}
            />
          </div>

          {isLoading && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button
            onClick={handleGenerate}
            isLoading={isLoading}
            disabled={files.length === 0}
            className="w-full"
          >
            {translations.common.generate}
          </Button>
        </div>
      </Card>

      {results.length > 0 && (
        <Card title={`Eredmények (${results.length})`}>
          <div className="space-y-4">
            {results.length > 1 && (
              <Button
                variant="outline"
                onClick={handleDownloadAllChecksums}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Összes checksum letöltése
              </Button>
            )}

            {results.map((result, index) => (
              <div
                key={index}
                className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {result.fileName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {result.algorithm} • {(result.inputLength / 1024 / 1024).toFixed(2)} MB • {result.durationMs.toFixed(2)} ms
                    </p>
                  </div>
                  <div className="flex gap-2 ml-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(index, result.hash)}
                    >
                      {copiedIndex === index ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadChecksum(result)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                  <p className="font-mono text-sm break-all text-gray-900 dark:text-white">
                    {result.hash}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

